import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import merge from 'lodash.merge';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers, FormikProps, FormikErrors } from 'formik';
import { useHistory, useParams } from 'react-router-dom';

import Tab from '@material-ui/core/Tab';
import Container from '@material-ui/core/Container';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import Typography from '@material-ui/core/Typography';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';

import { scrollToTop } from 'helpers/helpers';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import StopModal from 'components/Modals/StopModal/StopModal';
import Background from 'components/Layout/Background';
import ControlButtons from 'components/ControlButtons';
import Tabs from 'components/CustomTabs/CustomTabs';
import FormCheckboxGroup from 'components/FormCheckboxGroup/FormCheckboxGroup';
import FormRadioGroup from 'components/FormRadioGroup';
//import transformForRadio from 'components/FormRadioGroup/transformForRadio';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { QuestionnaireQuestions } from 'common/interfaces';
import { WorkflowQuestionnaireStore } from 'stores/workflowStores/workflowQuestionnaireStore/newWorkflowQuestionnaireStore';
import { Params, ACCOUNTS_EDIT_FINANCE } from '../accountEditRoutes';
import { QA, structure } from './questionnaireStructure';
import { ACCOUNTS_PAGE } from 'routes';
import { initialValues } from 'stores/workflowStores/workflowQuestionnaireStore/model';
import { APP_CONFIG } from 'appConfig';
import { Currency } from 'enums';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inconsistency: {
      marginBottom: theme.spacing(2),
    },
    buttonIconRight: {
      marginLeft: theme.spacing(1),
    },
    answer: {
      display: 'block',
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(1),
    },
    input: {
      marginTop: theme.spacing(1),
    },
    info: {
      padding: theme.spacing(2, 0),
      fontSize: '14px',
      fontWeight: 400,
      color: '#58585a',
    },
    wrapper: {
      '@media (max-width: 1480px)': {
        position: 'relative',
        paddingTop: theme.spacing(6),
      },
    },
    skip: {
      position: 'absolute',
      right: theme.spacing(2),

      '@media (max-width: 1480px)': {
        right: 0,
        top: 0,
      },
    },
  }),
);

interface Props {
  store: WorkflowQuestionnaireStore;
  readOnly?: boolean;
  detailMode?: boolean;
  currency: Currency;
}

export const checkCondition = (values: QuestionnaireQuestions, questionIndex: string, answerIndex: string): boolean => {
  return (
    values[questionIndex] !== undefined &&
    ((Array.isArray(values[questionIndex]) && values[questionIndex].includes(answerIndex)) ||
      values[questionIndex] === answerIndex)
  );
};

export const scrubValues = (version: string, originalValues: QuestionnaireQuestions) => {
  const values = JSON.parse(JSON.stringify(originalValues));
  Object.keys(structure[version]).forEach((key) => {
    structure[version][Number(key)].forEach((question) => {
      if (
        question.condition &&
        !checkCondition(values, question.condition.questionIndex, question.condition.answerIndex)
      ) {
        delete values[question.name];
      }
    });
  });
  return values;
};

const detailDefault = (version: string, initial: { [key: string]: string | [] }) => {
  const newValues = JSON.parse(JSON.stringify(initial));
  Object.keys(structure[version]).forEach((key) => {
    structure[version][Number(key)].forEach((q) => {
      if (q.detailDefault) {
        newValues[q.name.toString(10)] = q.detailDefault;
      }
    });
  });
  return newValues;
};

const Questionnaire: React.FC<Props> = ({ store, readOnly, detailMode, currency }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const history = useHistory();
  const tabs = store.version ? Object.keys(structure[store.version]).length : 0;
  const [detailInfo, setDetailInfo] = useState<null | boolean>(null);

  const handleSubmit = async (values: QuestionnaireQuestions, formikHelpers: FormikHelpers<QuestionnaireQuestions>) => {
    await store.handleSubmitQuestionnaire(scrubValues(store.version, values), formikHelpers);
    scrollToTop();
  };

  const handleBack = () => {
    if (store.tab > 0) {
      store.setTab(store.tab - 1);
      scrollToTop();
    } else {
      history.push(ACCOUNTS_EDIT_FINANCE(accountId));
    }
  };

  const handleNextStep = async (formikProps: FormikProps<QuestionnaireQuestions>) => {
    const errors = await formikProps.validateForm();

    if (Object.keys(errors).length === 0) {
      await store.check(scrubValues(store.version, formikProps.values));
    } else {
      scrollToTop();
    }
  };

  const validate = (values: QuestionnaireQuestions) => {
    const error = 'missing';
    let errors: FormikErrors<QuestionnaireQuestions> = {};

    structure[store.version][store.tab].forEach((question) => {
      if (
        !values[question.name]?.length &&
        (!question.condition ||
          checkCondition(values, question.condition.questionIndex, question.condition.answerIndex))
      ) {
        errors[question.name] = error;
      }
    });

    return errors;
  };

  const getLabelFromValue = (name: string, value: string | string[]) => {
    return value === '' ? '…' : t(`accountInvestment_v${store.version}_${name}_${value}`);
  };

  const renderQuestonsAndAnswers = (block: QA[], formikProps: FormikProps<QuestionnaireQuestions>) => {
    return (
      <>
        {block.map((q) => {
          let component = null;
          if (!q.condition || checkCondition(formikProps.values, q.condition.questionIndex, q.condition.answerIndex)) {
            switch (q.type) {
              case 'radio':
                component = (
                  <FormRadioGroup
                    key={`${q.name}`}
                    name={`${q.name}`}
                    label={t(
                      `accountInvestment_v${store.version}_${q.name}label`,
                      q.valueInTitle
                        ? { value: getLabelFromValue(q.valueInTitle, formikProps.values[q.valueInTitle]) }
                        : undefined,
                    )}
                    formikProps={formikProps}
                    values={
                      //transformForRadio(
                      new Array(q.options).fill(0).map((_value, i) => ({
                        value: q.order ? q.order[i] : `${i + 1}`,
                        label: t(
                          `accountInvestment_v${store.version}_${q.name}_${q.order ? q.order[i] : i + 1}${
                            q.currency ? '_' + currency : ''
                          }`,
                        ),
                      }))
                      //)
                    }
                    images={q.images}
                    disabledAnswers={!store.completePlan && q.disabledInPortfolio ? q.disabledInPortfolio : undefined}
                    fast={false}
                    extended={q.extended}
                    escapeValues={false}
                    tooltip={
                      q.tooltip
                        ? t(`accountInvestment_v${store.version}_${q.name}_tooltip`, {
                            interpolation: { escapeValue: false },
                          })
                        : undefined
                    }
                    info={q.info ? t(`accountInvestment_v${store.version}_${q.name}_info`) : undefined}
                  />
                );
                break;
              case 'check':
                component = (
                  <FormCheckboxGroup
                    key={`${q.name}`}
                    name={`${q.name}`}
                    label={t(
                      `accountInvestment_v${store.version}_${q.name}label`,
                      q.valueInTitle
                        ? { value: getLabelFromValue(q.valueInTitle, formikProps.values[q.valueInTitle]) }
                        : undefined,
                    )}
                    values={formikProps.values[q.name] as string[]}
                    errorMessage={formikProps.errors[q.name]}
                    options={new Array(q.options)
                      .fill(0)
                      .map((_value, i) =>
                        t(
                          `accountInvestment_v${store.version}_${q.name}_${q.order ? q.order[i] : i + 1}${
                            q.currency ? '_' + currency : ''
                          }`,
                        ),
                      )}
                    notaIndex={q.notaIndex}
                    fast={false}
                    tooltip={
                      q.tooltip
                        ? t(`accountInvestment_v${store.version}_${q.name}_tooltip`, {
                            interpolation: { escapeValue: false },
                          })
                        : undefined
                    }
                    info={q.info ? t(`accountInvestment_v${store.version}_${q.name}_info`) : undefined}
                  />
                );
                break;
            }
          }
          return component;
        })}
      </>
    );
  };

  // value?: number should be value: number, but the @material-ui typing is bugged
  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: number) => {
    if (value !== undefined) {
      if (APP_CONFIG.uiSwitchTabs || value < store.tab) {
        store.setTab(value);
      }
    }
  };

  const stopWorkflow = async (questionNumber: string) => {
    const success = await store.stopWorkflow(questionNumber);
    if (success) {
      history.push(ACCOUNTS_PAGE);
    }
  };

  if (!store.data) return null;

  return (
    <>
      <Container maxWidth="md" className={classes.wrapper}>
        {!detailMode && (
          <Button
            onClick={store.skipQuestionnaire}
            color="primary"
            variant="contained"
            endIcon={<DoubleArrowIcon />}
            className={classes.skip}
          >
            {t('investmentQuestionnaire_skip')}
          </Button>
        )}

        <Background>
          <Tabs value={store.tab} onChange={handleTabChange}>
            {new Array(tabs).fill(0).map((tab, i) => (
              <Tab
                label={t(`accountInvestment_v${store.version}_panel${i}Heading`)}
                id={`investment-${i}-tab`}
                key={`investment-${i}-tab`}
              />
            ))}
          </Tabs>

          <Formik
            validate={validate}
            validateOnBlur={false}
            validateOnChange={false}
            initialValues={
              merge(
                {},
                detailMode ? detailDefault(store.version, initialValues[store.version]) : initialValues[store.version],
                store.data,
              ) as QuestionnaireQuestions
            }
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formikProps: FormikProps<QuestionnaireQuestions>) => (
              <>
                <div className={classes.info}>{t(`accountInvestment_v${store.version}_panel${store.tab}Info`)}</div>
                <div style={{ paddingTop: '16px' }}>
                  <Form id="risk-form">
                    {store.completePlan != null &&
                      renderQuestonsAndAnswers(structure[store.version][store.tab], formikProps)}
                  </Form>
                </div>

                {!readOnly && (
                  <ControlButtons
                    key={store.tab !== tabs - 1 ? 'button-next' : 'button-submit'}
                    form="risk-form"
                    backText={store.tab === 0 ? t('accountStepper_financialQuestionnaire') : undefined}
                    onBack={() => handleBack()}
                    nextText={
                      store.tab === tabs - 1
                        ? t('accountInvestment_evaluationShort')
                        : detailMode
                        ? t('accountInvestment_confirm')
                        : undefined
                    }
                    onNext={store.tab !== tabs - 1 ? () => handleNextStep(formikProps) : undefined}
                    isSubmitting={formikProps.isSubmitting || formikProps.isValidating}
                    hideBack={store.tab === 0 && detailMode}
                  />
                )}
                {structure[store.version][store.tab].map((question) => {
                  if (question.stop && checkCondition(formikProps.values, `${question.name}`, question.stop)) {
                    return (
                      <StopModal
                        key={`stop_${question.name}`}
                        onReset={() => {
                          formikProps.setFieldValue(`${question.name}`, '');
                        }}
                        onStop={() => stopWorkflow(`${question.name}`)}
                      />
                    );
                  } else return null;
                })}
                {detailMode &&
                  structure[store.version][store.tab].map((question) => {
                    if (
                      detailInfo !== false &&
                      question.detailInfo &&
                      checkCondition(formikProps.values, `${question.name}`, question.detailInfo) &&
                      store.data &&
                      store.data[question.name] !== question.detailInfo
                    ) {
                      setDetailInfo(true);
                      return (
                        <ConfirmDialog
                          key={`detailInfo_${question.name}`}
                          title={null}
                          btnConfirmText="investmentQuestionnaire_esg_preferEsg"
                          btnCloseText="investmentQuestionnaire_esg_keepOriginal"
                          isOpen={!!detailInfo}
                          confirm={() => {
                            store.setPreferEsg();
                            setDetailInfo(false);
                          }}
                          close={() => setDetailInfo(false)}
                        >
                          <Typography>{t('investmentQuestionnaire_esg_change')}</Typography>
                        </ConfirmDialog>
                      );
                    } else return null;
                  })}

                <Dialog open={store.changesDialog} maxWidth="sm" fullWidth>
                  <DialogHeader>{t('accountInvestment_changes')}</DialogHeader>
                  <DialogContent>
                    <DialogContentText>
                      <strong>{t('accountInvestment_changes_info')}</strong>
                    </DialogContentText>
                    {store.changes &&
                      store.previousAnswers &&
                      store.changes.map((i) => {
                        const q = structure[store.version][store.tab].find((q) => q.name.toString() === i.key);
                        const vit = q?.valueInTitle;
                        const c = q?.currency;

                        return (
                          <div key={i.key} className={classes.inconsistency}>
                            <strong
                              key={i.key}
                              dangerouslySetInnerHTML={{
                                __html: t(`accountInvestment_v${store.version}_${i.key}label`, {
                                  value: vit ? getLabelFromValue(vit, formikProps.values[vit]) : undefined,
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                            <span className={classes.answer}>
                              {t('accountInvestment_prevValue')}:&nbsp;
                              {Array.isArray(store.previousAnswers![i.key])
                                ? (store.previousAnswers![i.key] as string[])
                                    .map((a) => t(`accountInvestment_v${store.version}_${i.key}_${a}`))
                                    .join(', ')
                                : t(
                                    `accountInvestment_v${store.version}_${i.key}_${store.previousAnswers![i.key]}${
                                      c ? '_' + currency : ''
                                    }`,
                                  )}
                            </span>
                            <span className={classes.answer}>
                              {t('accountInvestment_currentValue')}:&nbsp;
                              <strong>
                                {Array.isArray(i.current)
                                  ? (i.current as string[])
                                      .map((a) => t(`accountInvestment_v${store.version}_${i.key}_${a}`))
                                      .join(', ')
                                  : t(
                                      `accountInvestment_v${store.version}_${i.key}_${i.current}${
                                        c ? '_' + currency : ''
                                      }`,
                                    )}
                              </strong>
                            </span>
                            <TextField
                              onChange={(e) => store.updateChange(i.key, e.target.value)}
                              label={t('accountInvestment_giveReason')}
                              className={classes.input}
                            />
                          </div>
                        );
                      })}
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => {
                        store.hideChangesDialog();
                        store.updatePreviousAnswers();
                        store.checkInconsistencies();
                      }}
                      color="primary"
                      variant="contained"
                      disabled={!store.answers}
                    >
                      {t('general_continue')}
                      <ArrowForwardIos fontSize="small" className={classes.buttonIconRight} />
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Formik>
        </Background>
      </Container>

      <Dialog open={store.inconsistencyInfo} maxWidth="sm" fullWidth>
        <DialogHeader>{t('accountInvestment_inconsistencies')}</DialogHeader>
        <DialogContent>
          <DialogContentText>
            <strong>{t('accountInvestment_inconsistency_fix')}</strong>
          </DialogContentText>
          {store.inconsistencies &&
            store.inconsistencies.map((i) => {
              return (
                <div key={i.key} className={classes.inconsistency}>
                  <span
                    key={i.key}
                    dangerouslySetInnerHTML={{
                      __html: t(`accountInvestment_inconsistency_v${store.version}_${i.key}`, {
                        interpolation: { escapeValue: false },
                      }),
                    }}
                  />
                  <TextField
                    onChange={(e) => store.updateReason(i.key, i.questions, e.target.value)}
                    label={t('accountInvestment_giveReason')}
                    className={classes.input}
                  />
                </div>
              );
            })}
        </DialogContent>
        <DialogActions>
          <Button onClick={store.hideInconsistencyInfo} color="primary" variant="contained" disabled={!store.answers}>
            {t('general_continue')}
            <ArrowForwardIos fontSize="small" className={classes.buttonIconRight} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default compose(inject('store'), observer)(Questionnaire);
